import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore, useIsHydrated, type UserRole } from '../store/useAuthStore';

/**
 * Role hierarchy for RBAC
 * Higher roles inherit all permissions from lower roles
 */
const ROLE_HIERARCHY: Record<UserRole, UserRole[]> = {
  SUPER_ADMIN: ['SUPER_ADMIN', 'ADMIN', 'USER'],
  ADMIN: ['ADMIN', 'USER'],
  USER: ['USER'],
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Optional: Required roles to access the route
   * If not specified, any authenticated user can access
   * Role hierarchy is respected: SUPER_ADMIN can access ADMIN routes
   */
  requiredRoles?: UserRole[];
  /**
   * Redirect path when not authenticated
   * Defaults to /login
   */
  redirectTo?: string;
}

/**
 * ProtectedRoute Component
 * 
 * Guards routes that require authentication.
 * Optionally restricts access based on user roles.
 * 
 * Usage:
 * ```tsx
 * <Route 
 *   path="/admin" 
 *   element={
 *     <ProtectedRoute requiredRoles={['ADMIN']}>
 *       <AdminDashboard />
 *     </ProtectedRoute>
 *   } 
 * />
 * ```
 */
export function ProtectedRoute({
  children,
  requiredRoles,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuthStore();
  const isHydrated = useIsHydrated();
  const location = useLocation();

  // Wait for store hydration before making auth decisions
  // This prevents flash of login page on refresh
  if (!isHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    // Save the attempted location for redirect after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role requirements if specified (with hierarchy support)
  if (requiredRoles && requiredRoles.length > 0 && user) {
    // Get all effective roles for the user based on hierarchy
    const userEffectiveRoles = ROLE_HIERARCHY[user.role] || [user.role];
    
    // Check if user's effective roles include at least one required role
    const hasRequiredRole = requiredRoles.some((role) =>
      userEffectiveRoles.includes(role)
    );

    if (!hasRequiredRole) {
      // User is authenticated but doesn't have required role
      // Redirect to unauthorized page or home
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}

/**
 * PublicOnlyRoute Component
 * 
 * For routes that should only be accessible to unauthenticated users
 * (e.g., login page - redirect to dashboard if already logged in)
 */
interface PublicOnlyRouteProps {
  children: React.ReactNode;
  /**
   * Redirect path when already authenticated
   * Defaults to /admin
   */
  redirectTo?: string;
}

export function PublicOnlyRoute({
  children,
  redirectTo = '/admin',
}: PublicOnlyRouteProps) {
  const { isAuthenticated } = useAuthStore();
  const isHydrated = useIsHydrated();
  const location = useLocation();

  // Wait for hydration
  if (!isHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Already authenticated - redirect to intended destination or default
  if (isAuthenticated) {
    // Check if there's a saved location to redirect to
    const from = (location.state as { from?: Location })?.from?.pathname || redirectTo;
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}

/**
 * PermissionRoute Component
 *
 * Guards admin sub-routes based on granular permissions.
 * SUPER_ADMIN always passes. Regular ADMIN must have at least
 * one permission matching the required module (e.g. "members").
 * If denied, redirects to /admin (dashboard) instead of /unauthorized.
 */
interface PermissionRouteProps {
  children: React.ReactNode;
  /** Required permission module key (e.g. "members", "donations") */
  module: string;
}

export function PermissionRoute({ children, module }: PermissionRouteProps) {
  const { canAccess } = usePermissions();

  if (canAccess(module)) return <>{children}</>;

  return <Navigate to="/admin" replace />;
}

export default ProtectedRoute;
